import { useState, useEffect } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { NAV, SITE } from "@/lib/site";
import { useAppointment } from "@/components/AppointmentContext";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { openDialog } = useAppointment();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background,box-shadow,padding] duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="navbar"
    >
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className={`flex items-center justify-between rounded-full px-4 sm:px-6 transition-all duration-500 ${
          scrolled ? "glass shadow-soft h-14" : "h-16"
        }`}>
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="logo-link">
            <Logo className="h-10 w-10 shrink-0" tone={scrolled ? "dark" : "light"} id="nav" />
            <span className="leading-none">
              <span className={`block font-display text-lg sm:text-xl tracking-tight ${scrolled ? "text-navy" : "text-white"}`}>Beacon</span>
              <span className={`mt-0.5 block text-[0.58rem] font-semibold uppercase tracking-[0.24em] ${scrolled ? "text-teal" : "text-teal-light"}`}>Counseling</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) =>
              n.children ? (
                <div key={n.label} className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}>
                  <button className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm transition-colors ${scrolled ? "text-navy/80 hover:text-navy hover:bg-navy-50" : "text-white/85 hover:text-white hover:bg-white/10"}`}>
                    {n.label} <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <AnimatePresence>
                    {servicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute left-0 top-full pt-3 w-64"
                      >
                        <div className="glass rounded-2xl shadow-premium p-2">
                          {n.children.map((c) => (
                            <NavLink key={c.to} to={c.to}
                              className="block rounded-xl px-4 py-2.5 text-sm text-navy/80 hover:bg-softblue/60 hover:text-navy transition-colors">
                              {c.label}
                            </NavLink>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <NavLink key={n.to} to={n.to}
                  className={({ isActive }) =>
                    `rounded-full px-4 py-2 text-sm transition-colors ${
                      isActive
                        ? (scrolled ? "text-teal font-medium" : "text-teal-light font-medium")
                        : (scrolled ? "text-navy/80 hover:text-navy hover:bg-navy-50" : "text-white/85 hover:text-white hover:bg-white/10")
                    }`}>
                  {n.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a href={SITE.phoneHref} className={`hidden xl:flex items-center gap-2 text-sm transition-colors mr-1 ${scrolled ? "text-navy/70 hover:text-teal" : "text-white/70 hover:text-teal-light"}`}>
              <Phone className="h-4 w-4" /> {SITE.phone}
            </a>
            <Button onClick={openDialog} data-testid="nav-schedule-btn"
              className="hidden sm:inline-flex rounded-full bg-teal hover:bg-teal-dark text-white px-5 h-10 shadow-soft hover:-translate-y-0.5 transition-transform">
              Free Consult
            </Button>
            <button className={`lg:hidden grid h-10 w-10 place-items-center rounded-full transition-colors ${scrolled ? "bg-navy-50 text-navy" : "bg-white/15 text-white"}`}
              onClick={() => setMobileOpen((o) => !o)} aria-label="Menu" data-testid="mobile-menu-btn">
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            className="lg:hidden mx-4 mt-2"
          >
            <div className="glass rounded-3xl shadow-premium p-4">
              {NAV.map((n) => (
                <div key={n.label}>
                  <NavLink to={n.to}
                    className="block rounded-xl px-4 py-3 text-navy font-medium hover:bg-softblue/50">
                    {n.label}
                  </NavLink>
                  {n.children && (
                    <div className="ml-3 border-l border-navy-100 pl-3">
                      {n.children.map((c) => (
                        <NavLink key={c.to} to={c.to}
                          className="block rounded-xl px-4 py-2 text-sm text-navy/70 hover:bg-softblue/40">
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <Button onClick={openDialog} className="mt-3 w-full rounded-full bg-teal hover:bg-teal-dark h-12">
                Schedule Your Free 15-Minute Consultation
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
